import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  CreditCard,
  Home,
  History,
  Wallet,
  Plus,
  Globe,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Карта и баланс' },
  { id: 'history', name: 'История', icon: History, description: 'Транзакции' },
  { id: 'profile', name: 'Профиль', icon: User, description: 'Личные данные' }
];

// Services for transactions
const SERVICES = [
  { name: 'Netflix', icon: '🎬', amount: 15.99, currency: '€' },
  { name: 'Spotify', icon: '🎵', amount: 10.99, currency: '€' },
  { name: 'ChatGPT Plus', icon: '🤖', amount: 20, currency: '$' },
  { name: 'YouTube Premium', icon: '📺', amount: 13.99, currency: '€' },
  { name: 'Apple Music', icon: '🍎', amount: 10.99, currency: '€' },
  { name: 'Midjourney', icon: '🎨', amount: 30, currency: '$' },
  { name: 'Booking.com', icon: '🏨', amount: 150, currency: '€' },
  { name: 'Uber', icon: '🚗', amount: 25, currency: '$' },
  { name: 'Airbnb', icon: '🏠', amount: 200, currency: '$' },
  { name: 'Adobe Creative', icon: '🎭', amount: 54.99, currency: '$' },
  { name: 'Figma', icon: '✏️', amount: 15, currency: '$' },
  { name: 'Steam', icon: '🎮', amount: 49.99, currency: '$' },
  { name: 'Amazon Prime', icon: '📦', amount: 14.99, currency: '$' },
  { name: 'Claude Pro', icon: '🧠', amount: 20, currency: '$' },
  { name: 'Notion', icon: '📝', amount: 10, currency: '$' },
];

export default function PlatiPomiruTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const [showSenders, setShowSenders] = useState(true);
  
  // Настройки карты
  const [cardBalance, setCardBalance] = useState('');
  const [cardCurrency, setCardCurrency] = useState('USD');
  const [transactions, setTransactions] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  // Генерация транзакций из OZON профиля + оплаты сервисов
  const generateTransactions = (profile) => {
    const ozonList = []; // Все транзакции из OZON
    
    // 1. Берём транзакции из OZON профиля
    const ozonTransactions = profile?.transactions || [];
    
    // Deposits из OZON → Выводы в "Плати по миру"
    const deposits = ozonTransactions.filter(t => t.type === 'deposit');
    deposits.forEach((tx, i) => {
      // Категория "Переводы" или "Пополнения" = СБП, иначе = карта
      const isSBP = tx.category === 'Пополнения' || tx.category === 'Переводы';
      const description = isSBP ? 'Вывод по СБП' : 'Вывод на карту';
      
      ozonList.push({
        id: tx.id || Date.now() + i,
        type: 'withdrawal',
        description,
        recipient: tx.method || (isSBP ? 'СБП' : 'Карта'),
        paymentMethod: isSBP ? 'СБП' : 'Карта',
        amountRub: tx.amount,
        amountUsd: Math.round(tx.amount / 95 * 100) / 100,
        currency: 'RUB',
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // Withdrawals из OZON → Пополнения в "Плати по миру"
    const withdrawals = ozonTransactions.filter(t => t.type === 'withdrawal');
    withdrawals.forEach((tx, i) => {
      // Категория "Переводы" или "Пополнения" = СБП, "Финансовые услуги" = карта
      const isSBP = tx.category === 'Пополнения' || tx.category === 'Переводы';
      const description = isSBP ? 'Пополнение по СБП' : 'Пополнение с карты';
      
      ozonList.push({
        id: tx.id || Date.now() + 500 + i,
        type: 'topup',
        description,
        sender: tx.method || (isSBP ? 'СБП' : 'Карта'),
        paymentMethod: isSBP ? 'СБП' : 'Карта',
        amountRub: tx.amount,
        amountUsd: Math.round(tx.amount / 95 * 100) / 100,
        currency: 'RUB',
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // Сортируем OZON транзакции по дате
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date(0);
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        return new Date(year, month - 1, day, hour, min);
      }
      return new Date(0);
    };
    
    ozonList.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // Генерация даты между двумя OZON транзакциями
    const generateDateBetween = (date1Str, date2Str) => {
      const date1 = parseDate(date1Str);
      const date2 = parseDate(date2Str);
      const minTime = Math.min(date1.getTime(), date2.getTime());
      const maxTime = Math.max(date1.getTime(), date2.getTime());
      const randomTime = minTime + Math.random() * (maxTime - minTime);
      const date = new Date(randomTime);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hour = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year}, ${hour}:${min}`;
    };
    
    // Генерация рандомной оплаты сервиса (сумма не больше maxAmount в $)
    const generateServicePayment = (dateStr, maxAmountUsd) => {
      const service = SERVICES[Math.floor(Math.random() * SERVICES.length)];
      // Сумма от 1$ до maxAmountUsd (но не больше 90$)
      const maxAllowed = Math.min(maxAmountUsd || 90, 90);
      const minAmount = Math.min(1, maxAllowed * 0.1);
      const randomAmount = Math.round((Math.random() * (maxAllowed - minAmount) + minAmount) * 100) / 100;
      const currency = Math.random() > 0.5 ? '$' : '€';
      return {
        id: Date.now() + Math.random() * 10000,
        type: 'payment',
        description: service.name,
        icon: service.icon,
        amount: randomAmount,
        currency: currency,
        status: 'completed',
        date: dateStr
      };
    };
    
    // 2. Генерируем рандомное количество оплат сервисов (от 10 до 50)
    const servicePaymentsCount = Math.floor(Math.random() * 41) + 10; // 10-50
    const servicePayments = [];
    
    // Определяем диапазон дат для оплат сервисов
    let minDateStr = null;
    let maxDateStr = null;
    
    if (ozonList.length > 0) {
      // Находим самую раннюю и самую позднюю даты OZON транзакций
      const sortedByDate = [...ozonList].sort((a, b) => parseDate(a.date) - parseDate(b.date));
      minDateStr = sortedByDate[0]?.date;
      maxDateStr = sortedByDate[sortedByDate.length - 1]?.date;
    }
    
    // Генерируем оплаты сервисов в этом диапазоне дат
    for (let i = 0; i < servicePaymentsCount; i++) {
      let dateStr;
      if (minDateStr && maxDateStr) {
        dateStr = generateDateBetween(minDateStr, maxDateStr);
      } else {
        // Если нет OZON транзакций, генерируем даты за последние 3 месяца
        const now = new Date();
        const threeMonthsAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        const randomTime = threeMonthsAgo.getTime() + Math.random() * (now.getTime() - threeMonthsAgo.getTime());
        const date = new Date(randomTime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        dateStr = `${day}.${month}.${year}, ${hour}:${min}`;
      }
      servicePayments.push(generateServicePayment(dateStr, 50));
    }
    
    // 3. Объединяем OZON транзакции и оплаты сервисов
    const finalTransactions = [...ozonList, ...servicePayments];
    
    // Сортируем по дате (новые сверху)
    finalTransactions.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // 4. ОБЯЗАТЕЛЬНО: Добавляем первое пополнение СБП в конец (самая ранняя дата - декабрь 2025)
    const randomDay = Math.floor(Math.random() * 28) + 1; // 1-28
    const randomHour = String(Math.floor(Math.random() * 14) + 8).padStart(2, '0'); // 08-22
    const randomMin = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    const firstDepositDate = `${String(randomDay).padStart(2, '0')}.12.2025, ${randomHour}:${randomMin}`;
    const firstDepositAmount = Math.floor(Math.random() * 3501) + 2500; // 2500-6000
    
    finalTransactions.push({
      id: Date.now() + 9999,
      type: 'topup',
      description: 'Пополнение по СБП',
      sender: 'Первое пополнение',
      amountRub: firstDepositAmount,
      amountUsd: Math.round(firstDepositAmount / 90),
      currency: 'RUB',
      status: 'completed',
      date: firstDepositDate,
      icon: '💳'
    });
    
    // Финальная сортировка по дате
    finalTransactions.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    return finalTransactions;
  };

  const handleSelectProfile = (profile) => {
    setSelectedProfile(profile);
    setDropdownOpen(false);
    const mixedTransactions = generateTransactions(profile);
    setTransactions(mixedTransactions);
    const balance = Math.floor(Math.random() * 500) + 50;
    setCardBalance(String(balance));
    
    const ozonTx = profile?.transactions || [];
    const withdrawalCount = ozonTx.filter(t => t.type === 'deposit').length; // deposits → выводы
    const depositCount = ozonTx.filter(t => t.type === 'withdrawal').length; // withdrawals → пополнения
    const serviceCount = mixedTransactions.length - depositCount - withdrawalCount;
    toast.success(`${depositCount} пополнений, ${withdrawalCount} выводов, ${serviceCount} оплат`);
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      setProfiles(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const regenerateTransactions = () => {
    if (!selectedProfile) return;
    const mixedTransactions = generateTransactions(selectedProfile);
    setTransactions(mixedTransactions);
    
    const ozonTx = selectedProfile?.transactions || [];
    const withdrawalCount = ozonTx.filter(t => t.type === 'deposit').length; // deposits → выводы
    const depositCount = ozonTx.filter(t => t.type === 'withdrawal').length; // withdrawals → пополнения
    const serviceCount = mixedTransactions.length - depositCount - withdrawalCount;
    toast.success(`${depositCount} пополнений, ${withdrawalCount} выводов, ${serviceCount} оплат`);
  };

  const generateRandomDate = (daysAgo) => {
    const now = new Date();
    const date = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generateCardNumber = () => {
    const segments = [];
    for (let i = 0; i < 4; i++) {
      segments.push(String(Math.floor(1000 + Math.random() * 9000)));
    }
    return segments.join(' ');
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}@mail.ru`;
    
    // Генерация дат для транзакций
    const generatedTransactions = transactions.map((tx, index) => ({
      ...tx,
      date: tx.date || generateRandomDate(index * 2 + Math.floor(Math.random() * 3))
    }));
    
    // Calculate date range from transactions
    let dateFrom = null;
    let dateTo = null;
    if (generatedTransactions.length > 0) {
      const dates = generatedTransactions
        .map(tx => {
          if (!tx.date) return null;
          const match = tx.date.match(/(\d{2})\.(\d{2})\.(\d{4})/);
          if (!match) return null;
          const [, day, month, year] = match;
          return { 
            original: `${day}.${month}.${year}`, 
            sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
          };
        })
        .filter(d => d !== null);
      
      if (dates.length > 0) {
        dates.sort((a, b) => a.sortValue - b.sortValue);
        dateFrom = dates[0].original;
        dateTo = dates[dates.length - 1].original;
      }
    }
    
    // Подсчёт статистики
    const totalTopups = generatedTransactions
      .filter(t => t.type === 'topup')
      .reduce((sum, t) => sum + (t.amountUsd || 0), 0);
    
    const totalPayments = generatedTransactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    
    const balance = parseFloat(cardBalance) || Math.floor(Math.random() * 500) + 50;
    
    // Генерация данных карты
    const cardNumber = generateCardNumber();
    const expMonth = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
    const expYear = String(new Date().getFullYear() + Math.floor(Math.random() * 4) + 1).slice(-2);
    const cvv = String(Math.floor(100 + Math.random() * 900));
    
    const data = {
      id: selectedProfile.id,
      oderId: selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000),
      userId: String(selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000)),
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone,
      email,
      card: {
        number: cardNumber,
        expiry: `${expMonth}/${expYear}`,
        cvv,
        currency: cardCurrency,
        balance,
        type: cardCurrency === 'EUR' ? 'Карта для подписок' : 'Карта для путешествий'
      },
      transactions: generatedTransactions,
      stats: {
        totalTopups: Math.round(totalTopups * 100) / 100,
        totalPayments: Math.round(totalPayments * 100) / 100,
        transactionCount: generatedTransactions.length
      },
      showSenders: showSenders,
      dateFrom,
      dateTo,
      verified: true,
      applePay: true,
      googlePay: true,
      registrationDate: generateRandomDate(Math.floor(Math.random() * 180) + 30)
    };
    
    localStorage.setItem('platipomiru_preview_data', JSON.stringify(data));
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    return `${window.location.origin}/templates/platipomiru/${selectedTemplate}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) window.open(url, '_blank');
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/platipomiru/archive`,
        { 
          data: generatedData,
          pages: ['main', 'history', 'profile']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Создаём ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `platipomiru_${generatedData.userId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива. Функция в разработке.");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="platipomiru-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg">
              <Globe className="w-5 h-5 text-white" />
            </div>
            Плати по миру
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор виртуальной карты
          </p>
        </div>
        <Button onClick={fetchProfiles} variant="outline">
          <RefreshCw className="w-4 h-4" />
          Обновить
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label>Страница</Label>
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${selectedTemplate === template.id ? 'text-blue-500' : 'text-slate-400'}`} />
                      <div className={`text-sm font-medium ${selectedTemplate === template.id ? 'text-blue-700' : 'text-slate-700'}`}>
                        {template.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label>Профиль OZON</Label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">ID: {selectedProfile.ozon_id}</div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {profiles.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => handleSelectProfile(profile)}
                        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-slate-50"
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{profile.name}</span>
                        {selectedProfile?.id === profile.id && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Card Settings */}
            <div className="space-y-3 mb-6">
              <Label>Настройки карты</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Баланс</Label>
                  <Input
                    placeholder="500"
                    value={cardBalance}
                    onChange={(e) => setCardBalance(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Валюта</Label>
                  <select
                    value={cardCurrency}
                    onChange={(e) => setCardCurrency(e.target.value)}
                    className="w-full p-2 rounded-lg border border-slate-200"
                  >
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                  </select>
                </div>
              </div>
              
              {/* Show Senders Checkbox */}
              <div className="flex items-center gap-3 mt-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="showSenders"
                  checked={showSenders}
                  onChange={(e) => setShowSenders(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="showSenders" className="text-sm text-slate-700 cursor-pointer">
                  Показывать отправителей
                </label>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Сформировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Transactions */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Транзакции</h2>
              <Button size="sm" variant="outline" onClick={regenerateTransactions} disabled={!selectedProfile}>
                <RefreshCw className="w-4 h-4 mr-1" /> Регенерировать
              </Button>
            </div>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  Выберите профиль для генерации транзакций
                </div>
              ) : (
                transactions.map((tx, index) => (
                  <div key={tx.id} className={`p-3 rounded-lg flex items-center justify-between ${
                    tx.type === 'topup' ? 'bg-green-50' : tx.type === 'withdrawal' ? 'bg-orange-50' : 'bg-slate-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                        tx.type === 'topup' ? 'bg-green-100' : tx.type === 'withdrawal' ? 'bg-orange-100' : 'bg-white border'
                      }`}>
                        {tx.type === 'topup' ? '💳' : tx.type === 'withdrawal' ? '💸' : tx.icon}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800 text-sm">{tx.description}</div>
                        <div className="text-xs text-slate-500">
                          {tx.type === 'topup' ? `Отправитель: ${tx.sender || 'СБП'}` : tx.type === 'withdrawal' ? `Отправитель: ${tx.recipient || 'Карта'}` : 'Оплата сервиса'}
                          {tx.date && ` • ${tx.date}`}
                        </div>
                      </div>
                    </div>
                    <div className={`font-semibold text-right ${tx.type === 'topup' ? 'text-green-600' : tx.type === 'withdrawal' ? 'text-orange-600' : 'text-slate-700'}`}>
                      {tx.type === 'topup' ? `+${tx.amountRub?.toLocaleString()} ₽` : tx.type === 'withdrawal' ? `-${tx.amountRub?.toLocaleString()} ₽` : `-${tx.amount} ${tx.currency}`}
                      {(tx.type === 'topup' || tx.type === 'withdrawal') && <div className="text-xs text-slate-400">≈ {tx.amountUsd} $</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        {generatedData && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <span className="text-white font-bold">{generatedData.firstName[0]}{generatedData.lastName[0]}</span>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{generatedData.fullName}</div>
                  <div className="text-sm text-slate-500">
                    Баланс: {generatedData.card.balance} {generatedData.card.currency === 'USD' ? '$' : '€'} • {generatedData.stats.transactionCount} транзакций
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={openPreview} className="bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700">
                    <ExternalLink className="w-4 h-4 mr-1" /> Открыть
                  </Button>
                  <Button variant="outline" onClick={copyLink}>
                    <Copy className="w-4 h-4 mr-1" /> Копировать
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={downloadArchive}
                    disabled={isDownloadingArchive}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    {isDownloadingArchive ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    Архив
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
